import React, { useState, useEffect } from 'react';
import supabase from '../../supabase';
import AddClientPopup from '../PopUpComponents/AddClientPopup';
import './ProjectForm.css';

const ProjectForm = ({ project, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    budget: '',
    project_type: 'Timeline',
    Tax_type: 'none',
    Tax_value: '',
    client_id: ''
  });
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showAddClientPopup, setShowAddClientPopup] = useState(false);
  
  // Add client form state (matching the structure in Clients.jsx)
  const [newClient, setNewClient] = useState({
    name: '',
    email: '',
    company: '',
    gst: '',
    address: ''
  });

  // Function to calculate status based on dates
  const calculateStatus = (startDate, endDate, projectType) => {
    if (projectType === 'MileStone') {
      return 'active';
    }

    if (!startDate || !endDate) {
      return 'Not Active';
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    if (end < today) {
      return 'completed';
    } else if (start <= today) {
      return 'active';
    } else {
      return 'Not Active';
    }
  };

  // Get current status based on form data
  const currentStatus = calculateStatus(
    formData.start_date, 
    formData.end_date, 
    formData.project_type
  );

  useEffect(() => {
    fetchClients();
    if (project) {
      setFormData({
        name: project.name || '',
        description: project.description || '',
        start_date: project.start_date || '',
        end_date: project.end_date || '',
        budget: project.budget || '',
        project_type: project.project_type || 'Timeline',
        Tax_type: project.Tax_type || 'none',
        Tax_value: project.Tax_value || '',
        client_id: project.client_id || ''
      });
    }
  }, [project]);

  const fetchClients = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        console.error('User not authenticated');
        return;
      }

      const { data, error } = await supabase
        .from('clients')
        .select('id, client_name, client_email')
        .eq('user_id', user.id);

      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'Tax_type' && value === 'none') {
      setFormData(prev => ({
        ...prev,
        Tax_value: ''
      }));
    }

    if (name === 'project_type' && value === 'MileStone') {
      setFormData(prev => ({
        ...prev,
        start_date: '',
        end_date: ''
      }));
    }
  };

  const validateForm = () => {
    const { name, description, budget, project_type, Tax_type, Tax_value, start_date, end_date } = formData;

    if (!name.trim()) {
      setMessage({ type: 'error', text: 'Project name is required' });
      return false;
    }

    if (!description.trim()) {
      setMessage({ type: 'error', text: 'Description is required' });
      return false;
    }

    if (!budget || parseFloat(budget) <= 0) {
      setMessage({ type: 'error', text: 'Valid budget is required' });
      return false;
    }

    if (project_type === 'Timeline') {
      if (!start_date) {
        setMessage({ type: 'error', text: 'Start date is required for Timeline projects' });
        return false;
      }
      if (!end_date) {
        setMessage({ type: 'error', text: 'End date is required for Timeline projects' });
        return false;
      }
      if (new Date(end_date) < new Date(start_date)) {
        setMessage({ type: 'error', text: 'End date must be after start date' });
        return false;
      }
    }

    if (Tax_type !== 'none' && (!Tax_value || parseFloat(Tax_value) < 0)) {
      setMessage({ type: 'error', text: 'Valid tax value is required' });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        setMessage({ type: 'error', text: 'You must be logged in to create a project' });
        setLoading(false);
        return;
      }

      // Calculate status automatically
      const calculatedStatus = calculateStatus(
        formData.start_date,
        formData.end_date,
        formData.project_type
      );

      const projectData = {
        name: formData.name,
        description: formData.description,
        status: calculatedStatus, // Automatically calculated status
        budget: parseFloat(formData.budget),
        project_type: formData.project_type,
        Tax_type: formData.Tax_type,
        user_id: user.id,
        created_by: user.id
      };

      if (formData.client_id) {
        projectData.client_id = formData.client_id;
      }

      if (formData.project_type === 'Timeline') {
        projectData.start_date = formData.start_date;
        projectData.end_date = formData.end_date;
      }

      if (formData.Tax_type !== 'none') {
        projectData.Tax_value = parseFloat(formData.Tax_value);
      }

      if (project) {
        // Update existing project
        const { data, error } = await supabase
          .from('projects')
          .update(projectData)
          .eq('id', project.id)
          .select();

        if (error) throw error;
        setMessage({ type: 'success', text: 'Project updated successfully!' });
      } else {
        // Create new project
        const { data, error } = await supabase
          .from('projects')
          .insert([projectData])
          .select();

        if (error) throw error;
        setMessage({ type: 'success', text: 'Project created successfully!' });
      }

      // Wait a moment to show success message, then close
      setTimeout(() => {
        if (onClose) onClose();
      }, 1500);

    } catch (error) {
      console.error('Error saving project:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save project' });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      name: '',
      description: '',
      start_date: '',
      end_date: '',
      budget: '',
      project_type: 'Timeline',
      Tax_type: 'none',
      Tax_value: '',
      client_id: ''
    });
    setMessage({ type: '', text: '' });
  };

  // Handler for AddClientPopup save
  const handleAddClientSave = async () => {
    if (!newClient.name.trim()) {
      alert('Client name is required');
      return;
    }

    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        alert('User not authenticated');
        return;
      }

      const clientData = {
        client_name: newClient.name.trim(),
        client_email: newClient.email.trim() || null,
        client_company_name: newClient.company.trim() || null,
        client_gst_number: newClient.gst.trim() || null,
        address: newClient.address.trim() || null,
        user_id: user.id,
      };

      const { data, error } = await supabase
        .from('clients')
        .insert([clientData])
        .select();

      if (error) {
        console.error('Error saving client:', error);
        alert('Error saving client: ' + error.message);
      } else {
        // Reset the new client form
        setNewClient({
          name: '',
          email: '',
          company: '',
          gst: '',
          address: ''
        });
        setShowAddClientPopup(false);
        
        // Refresh clients list
        await fetchClients();
        
        // Optionally select the newly created client
        if (data && data[0]) {
          setFormData(prev => ({
            ...prev,
            client_id: data[0].id
          }));
        }
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      alert('An unexpected error occurred');
    }
  };

  // Handler for AddClientPopup cancel
  const handleAddClientCancel = () => {
    setNewClient({
      name: '',
      email: '',
      company: '',
      gst: '',
      address: ''
    });
    setShowAddClientPopup(false);
  };

  // Helper function to get status badge styling
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active':
        return 'status-badge-active';
      case 'completed':
        return 'status-badge-completed';
      case 'Not Active':
        return 'status-badge-inactive';
      default:
        return 'status-badge-inactive';
    }
  };

  return (
    <>
      <div className="projectform-container">
        <div className="projectform-header">
          <div className="projectform-header-top">
            <button 
              onClick={onClose} 
              className="projectform-back-btn"
              type="button"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back to Projects
            </button>
          </div>
          <h1 className="projectform-title">
            {project ? 'Edit Project' : 'Create New Project'}
          </h1>
          <div className="projectform-divider"></div>
        </div>
        
        {message.text && (
          <div className={`projectform-message projectform-message-${message.type}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="projectform-form">
          <div className="projectform-row">
            <div className="projectform-field">
              <label htmlFor="name" className="projectform-label">
                Project Name <span className="projectform-required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="projectform-input"
                placeholder="Enter project name"
                required
              />
            </div>

            <div className="projectform-field">
              <label htmlFor="client_id" className="projectform-label">
                Client
              </label>
              <div className="projectform-client-wrapper">
                <select
                  id="client_id"
                  name="client_id"
                  value={formData.client_id}
                  onChange={handleChange}
                  className="projectform-select projectform-select-with-button"
                >
                  <option value="">Select a client (optional)</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.client_name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowAddClientPopup(true)}
                  className="projectform-add-client-btn"
                  title="Add New Client"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 3.33334V12.6667M3.33334 8H12.6667" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Add Client
                </button>
              </div>
            </div>
          </div>

          <div className="projectform-field">
            <label htmlFor="description" className="projectform-label">
              Description <span className="projectform-required">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="projectform-textarea"
              placeholder="Enter project description"
              rows="4"
              required
            />
          </div>

          <div className="projectform-row">
            <div className="projectform-field">
              <label htmlFor="project_type" className="projectform-label">
                Project Type <span className="projectform-required">*</span>
              </label>
              <select
                id="project_type"
                name="project_type"
                value={formData.project_type}
                onChange={handleChange}
                className="projectform-select"
                required
              >
                <option value="Timeline">Timeline</option>
                <option value="MileStone">MileStone</option>
              </select>
            </div>

            <div className="projectform-field">
              <label className="projectform-label">
                Status (Auto-calculated)
              </label>
              <div className={`projectform-status-display ${getStatusBadgeClass(currentStatus)}`}>
                <span className="status-indicator"></span>
                {currentStatus === 'Not Active' ? 'Not Active' : 
                 currentStatus === 'active' ? 'Active' : 'Completed'}
              </div>
              <p className="projectform-helper-text">
                {formData.project_type === 'MileStone' 
                  ? 'Milestone projects are always active'
                  : formData.start_date && formData.end_date
                    ? 'Status is based on start and end dates'
                    : 'Enter start and end dates to see status'}
              </p>
            </div>
          </div>

          {formData.project_type === 'Timeline' && (
            <div className="projectform-row">
              <div className="projectform-field">
                <label htmlFor="start_date" className="projectform-label">
                  Start Date <span className="projectform-required">*</span>
                </label>
                <input
                  type="date"
                  id="start_date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                  className="projectform-input"
                  required
                />
              </div>

              <div className="projectform-field">
                <label htmlFor="end_date" className="projectform-label">
                  End Date <span className="projectform-required">*</span>
                </label>
                <input
                  type="date"
                  id="end_date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleChange}
                  className="projectform-input"
                  required
                />
              </div>
            </div>
          )}

          <div className="projectform-row">
            <div className="projectform-field">
              <label htmlFor="budget" className="projectform-label">
                Budget <span className="projectform-required">*</span>
              </label>
              <input
                type="number"
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                className="projectform-input"
                placeholder="Enter budget amount"
                step="0.01"
                min="0"
                required
              />
            </div>

            <div className="projectform-field">
              <label htmlFor="Tax_type" className="projectform-label">
                Tax Type <span className="projectform-required">*</span>
              </label>
              <select
                id="Tax_type"
                name="Tax_type"
                value={formData.Tax_type}
                onChange={handleChange}
                className="projectform-select"
                required
              >
                <option value="none">None</option>
                <option value="GST">GST</option>
                <option value="IGST">IGST</option>
              </select>
            </div>
          </div>

          {formData.Tax_type !== 'none' && (
            <div className="projectform-field">
              <label htmlFor="Tax_value" className="projectform-label">
                Tax Value (%) <span className="projectform-required">*</span>
              </label>
              <input
                type="number"
                id="Tax_value"
                name="Tax_value"
                value={formData.Tax_value}
                onChange={handleChange}
                className="projectform-input"
                placeholder="Enter tax percentage"
                step="0.01"
                min="0"
                max="100"
                required
              />
            </div>
          )}

          <div className="projectform-actions">
            <button
              type="button"
              onClick={handleReset}
              className="projectform-button projectform-button-secondary"
              disabled={loading}
            >
              Reset
            </button>
            <button
              type="submit"
              className="projectform-button projectform-button-primary"
              disabled={loading}
            >
              {loading ? (project ? 'Updating...' : 'Creating...') : (project ? 'Update Project' : 'Create Project')}
            </button>
          </div>
        </form>
      </div>

      {showAddClientPopup && (
        <AddClientPopup
          newClient={newClient}
          setNewClient={setNewClient}
          onSave={handleAddClientSave}
          onCancel={handleAddClientCancel}
          isEditing={false}
          title="Add New Client"
          saveButtonText="Add Client"
        />
      )}
    </>
  );
};

export default ProjectForm;
